import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import Navbar from '@/components/Navbar';
import AddAttributeDialog from '@/components/AddAttributeDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  ChevronDown, ChevronRight, ArrowLeft, Layers, FolderOpen,
  Repeat, Hash, FileText, Code, Calendar, ToggleLeft, Type, Plus, Download
} from 'lucide-react';
import { toast } from 'sonner';

const TYPE_ICONS = {
  string: Type, code: Code, date: Calendar, number: Hash, integer: Hash,
  float: Hash, boolean: ToggleLeft, text: FileText,
};
const TYPE_COLORS = {
  string: 'bg-zinc-100 text-zinc-600', code: 'bg-blue-50 text-blue-700',
  date: 'bg-amber-50 text-amber-700', number: 'bg-emerald-50 text-emerald-700',
  integer: 'bg-emerald-50 text-emerald-700', float: 'bg-teal-50 text-teal-700',
  boolean: 'bg-purple-50 text-purple-700', text: 'bg-rose-50 text-rose-600',
};

function AttributeRow({ attr, navigate }) {
  const Icon = TYPE_ICONS[attr.data_type] || Type;
  const colorClass = TYPE_COLORS[attr.data_type] || 'bg-zinc-100 text-zinc-600';
  return (
    <button onClick={() => navigate(`/attributes/${attr.attribute_id}`)} className="w-full flex items-center gap-3 px-3 py-2 hover:bg-[#F3F4F6] transition-colors duration-200 text-left border-b border-[#E5E7EB] last:border-b-0" data-testid={`attr-row-${attr.attribute_id}`}>
      <Icon className="w-3.5 h-3.5 text-[#9CA3AF] shrink-0" strokeWidth={1.5} />
      <span className="text-sm text-[#0A0A0A] flex-1 truncate">{attr.name}</span>
      <span className={`text-[10px] px-1.5 py-0.5 font-medium ${colorClass}`} style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{attr.data_type}</span>
      {attr.is_required && <span className="text-[10px] px-1.5 py-0.5 bg-red-50 text-red-600 font-medium">REQ</span>}
      {attr.valueset && <span className="text-[10px] px-1.5 py-0.5 bg-[#002FA7]/5 text-[#002FA7] font-medium truncate max-w-[120px]">{attr.valueset.name}</span>}
    </button>
  );
}

function GroupSection({ group, navigate }) {
  const [expanded, setExpanded] = useState(true);
  return (
    <div className="border-l border-[#E5E7EB] ml-4" data-testid={`group-${group.group_id}`}>
      <button onClick={() => setExpanded(p => !p)} className="flex items-center gap-2 px-3 py-2 w-full text-left hover:bg-[#F9FAFB]" data-testid={`group-toggle-${group.group_id}`}>
        {expanded ? <ChevronDown className="w-3.5 h-3.5 text-[#9CA3AF]" /> : <ChevronRight className="w-3.5 h-3.5 text-[#9CA3AF]" />}
        <FolderOpen className="w-3.5 h-3.5 text-[#4B5563]" strokeWidth={1.5} />
        <span className="text-xs font-semibold text-[#4B5563] uppercase tracking-[0.1em]">{group.name}</span>
        {group.is_repeating && <span className="text-[9px] px-1 py-0 bg-amber-50 text-amber-600 font-medium">REPEATING</span>}
        <span className="text-[10px] text-[#9CA3AF] ml-auto">{group.attributes.length}</span>
      </button>
      {expanded && <div className="ml-4">{group.attributes.map(attr => <AttributeRow key={attr.attribute_id} attr={attr} navigate={navigate} />)}</div>}
    </div>
  );
}

function EntitySection({ entity, navigate, schemaId }) {
  const [expanded, setExpanded] = useState(true);
  const totalAttrs = (entity.groups || []).reduce((s, g) => s + g.attributes.length, 0) + (entity.ungrouped_attributes || []).length;
  return (
    <div className="border border-[#E5E7EB] mb-px" data-testid={`entity-${entity.entity_id}`}>
      <div className="flex items-center gap-3 px-4 py-3 bg-[#F9FAFB] hover:bg-[#F3F4F6]">
        <button onClick={() => setExpanded(p => !p)} className="text-[#4B5563]" data-testid={`entity-toggle-${entity.entity_id}`}>
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        <Layers className="w-4 h-4 text-[#002FA7]" strokeWidth={1.5} />
        <span className="text-sm font-semibold text-[#0A0A0A] flex-1" style={{ fontFamily: 'Chivo, sans-serif' }}>{entity.name}</span>
        {entity.is_repeating && <Badge variant="outline" className="text-[9px] h-4 px-1.5 rounded-none border-amber-300 text-amber-700 bg-amber-50 font-semibold"><Repeat className="w-2.5 h-2.5 mr-0.5" /> REPEATING</Badge>}
        <span className="text-xs text-[#9CA3AF]">{totalAttrs} attrs</span>
        <Button variant="ghost" size="sm" className="h-6 px-2 rounded-none text-[#002FA7] text-[10px]" onClick={() => navigate(`/entities/${entity.entity_id}/doc?schema=${schemaId}`)} data-testid={`entity-doc-${entity.entity_id}`}>
          <FileText className="w-3 h-3 mr-1" /> Docs
        </Button>
      </div>
      {expanded && (
        <div>
          {(entity.groups || []).map(group => <GroupSection key={group.group_id} group={group} navigate={navigate} />)}
          {(entity.ungrouped_attributes || []).length > 0 && (
            <div className="ml-4 border-l border-[#E5E7EB]">
              <div className="px-3 py-1.5"><span className="text-[10px] text-[#9CA3AF] uppercase tracking-[0.1em] font-semibold">Direct attributes</span></div>
              {entity.ungrouped_attributes.map(attr => <AttributeRow key={attr.attribute_id} attr={attr} navigate={navigate} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function SchemaDetailPage() {
  const { schemaId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('edit_elements');
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addAttrOpen, setAddAttrOpen] = useState(false);
  const [allEntities, setAllEntities] = useState([]);

  const fetchSchema = useCallback(() => {
    api.get(`/schemas/${schemaId}/full`).then(r => setSchema(r.data)).catch(console.error).finally(() => setLoading(false));
  }, [schemaId]);

  useEffect(() => { fetchSchema(); }, [fetchSchema]);
  useEffect(() => { api.get('/entities').then(r => setAllEntities(r.data)).catch(() => {}); }, []);

  if (loading) {
    return (<div className="min-h-screen bg-white"><Navbar /><div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-zinc-300 border-t-zinc-900 rounded-full animate-spin" /></div></div>);
  }
  if (!schema) {
    return (<div className="min-h-screen bg-white"><Navbar /><div className="max-w-4xl mx-auto px-6 py-12 text-center text-[#9CA3AF]">Schema not found</div></div>);
  }

  const totalAttrs = (schema.entities || []).reduce((s, e) => {
    return s + (e.groups || []).reduce((gs, g) => gs + g.attributes.length, 0) + (e.ungrouped_attributes || []).length;
  }, 0);

  const downloadJsonSchema = async () => {
    try {
      const r = await api.get(`/schemas/${schemaId}/json-schema`);
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${schema?.code || 'schema'}_json_schema.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('JSON Schema downloaded');
    } catch { toast.error('Failed to download'); }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="schema-detail-page">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 py-6">
        <div className="flex items-center gap-2 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate('/schemas')} className="h-7 px-2 rounded-none text-[#4B5563]" data-testid="back-to-schemas">
            <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Schemas
          </Button>
          <span className="text-[#9CA3AF]">/</span>
          <span className="text-sm font-medium text-[#0A0A0A]">{schema.name}</span>
        </div>

        <div className="border-b border-[#E5E7EB] pb-6 mb-6">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-[#0A0A0A] mb-1" style={{ fontFamily: 'Chivo, sans-serif' }}>{schema.name}</h1>
              <p className="text-sm text-[#4B5563]">{schema.description}</p>
            </div>
            <div className="flex items-center gap-4 text-xs text-[#4B5563]">
              <div className="text-center">
                <div className="text-xl font-bold text-[#0A0A0A]" style={{ fontFamily: 'Chivo, sans-serif' }}>{schema.entities?.length || 0}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF]">Entities</div>
              </div>
              <div className="w-px h-8 bg-[#E5E7EB]" />
              <div className="text-center">
                <div className="text-xl font-bold text-[#0A0A0A]" style={{ fontFamily: 'Chivo, sans-serif' }}>{totalAttrs}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF]">Attributes</div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-[#9CA3AF]">Schema Structure</h2>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={downloadJsonSchema} className="rounded-none text-xs h-8" data-testid="download-json-schema-btn">
              <Download className="w-3.5 h-3.5 mr-1.5" /> JSON Schema
            </Button>
            {canEdit && (
              <>
                <Button variant="outline" size="sm" onClick={() => navigate(`/schemas/${schemaId}/import`)} className="rounded-none text-xs h-8 border-[#002FA7] text-[#002FA7]" data-testid="import-attributes-btn">
                  <Plus className="w-3.5 h-3.5 mr-1.5" /> Import / Manage Attributes
                </Button>
                <Button variant="outline" size="sm" onClick={() => setAddAttrOpen(true)} className="rounded-none text-xs h-8" data-testid="add-attribute-btn">
                  <Plus className="w-3.5 h-3.5 mr-1.5" /> Create New Attribute
                </Button>
              </>
            )}
          </div>
        </div>
        <div data-testid="entity-tree">
          {(schema.entities || []).map(entity => (
            <EntitySection key={entity.entity_id} entity={entity} navigate={navigate} schemaId={schemaId} />
          ))}
          {(!schema.entities || schema.entities.length === 0) && (
            <div className="text-center py-12 text-sm text-[#9CA3AF] border border-dashed border-[#E5E7EB]">
              No attributes mapped to this schema yet.
            </div>
          )}
        </div>
      </div>

      <AddAttributeDialog
        open={addAttrOpen}
        onOpenChange={setAddAttrOpen}
        entities={allEntities}
        schemaId={schemaId}
        schemaName={schema.name}
        onCreated={() => fetchSchema()}
      />
    </div>
  );
}
